import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface KeyboardShortcutsConfig {
  onFocusSearch?: () => void;
  onNewItem?: () => void;
  onToggleHelp?: () => void;
}

/**
 * Global keyboard shortcuts for the Alliance WMT app.
 *
 * Shortcuts:
 *   /  or Ctrl+K  — Focus search
 *   ?             — Toggle help overlay
 *   n             — New item (page-specific)
 *   Escape        — Close modals/overlays
 *   g then d      — Go to Dashboard
 *   g then c      — Go to Contracts
 *   g then p      — Go to Projects
 *   g then t      — Go to Tasks
 *   g then w      — Go to Workload
 *   g then a      — Go to Admin
 */
export function useKeyboardShortcuts({ onFocusSearch, onNewItem, onToggleHelp }: KeyboardShortcutsConfig = {}) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    const isEditing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;

    // Ctrl/Cmd+K always focuses search, even in input
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      onFocusSearch?.();
      return;
    }

    // Skip if user is typing in an input
    if (isEditing) return;

    switch (e.key) {
      case '/':
        e.preventDefault();
        onFocusSearch?.();
        break;
      case '?':
        e.preventDefault();
        onToggleHelp?.();
        break;
      case 'n':
        if (!e.ctrlKey && !e.metaKey && !e.altKey) {
          onNewItem?.();
        }
        break;
      case 'g':
        // Start navigation mode — listen for the next key
        const handler = (e2: KeyboardEvent) => {
          document.removeEventListener('keydown', handler);
          const navMap: Record<string, string> = {
            d: '/', c: '/contracts', p: '/projects', t: '/tasks',
            w: '/workload', a: '/admin', f: '/workforce', e: '/employees/capacity',
          };
          const dest = navMap[e2.key];
          if (dest && dest !== location.pathname) {
            e2.preventDefault();
            navigate(dest);
          }
        };
        document.addEventListener('keydown', handler, { once: true });
        // Auto-remove after 1s if no follow-up key
        setTimeout(() => document.removeEventListener('keydown', handler), 1000);
        break;
    }
  }, [navigate, location.pathname, onFocusSearch, onNewItem, onToggleHelp]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

export const SHORTCUT_GROUPS = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['g', 'd'], description: 'Go to Dashboard' },
      { keys: ['g', 'c'], description: 'Go to Contracts' },
      { keys: ['g', 'p'], description: 'Go to Projects' },
      { keys: ['g', 't'], description: 'Go to Tasks' },
      { keys: ['g', 'w'], description: 'Go to Workload' },
      { keys: ['g', 'e'], description: 'Go to Capacity' },
      { keys: ['g', 'f'], description: 'Go to Workforce Gantt' },
      { keys: ['g', 'a'], description: 'Go to Admin' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['/'], description: 'Focus search' },
      { keys: ['Ctrl', 'K'], description: 'Focus search (works in inputs)' },
      { keys: ['n'], description: 'New item (page-specific)' },
      { keys: ['?'], description: 'Toggle this help' },
      { keys: ['Esc'], description: 'Close modals/overlays' },
    ],
  },
];
